import React from "react";
import { ColorSwatch, Flex, Paper, Text, Title } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import { PieChart, Pie, Cell, ResponsiveContainer, PieLabel } from "recharts";

import useReportStore from "../../../store/useReportStore";
import { RoadmapSkill } from "../../../types/api";

const skillColors = ["#de57cc", "#a855f7", "#6f40e2", "#3a8bd9"];

function mapRoadmapSkillsToMaster(
  roadmapSkills: RoadmapSkill[]
): { name: string; color: string; value: number; text: string }[] {
  return roadmapSkills.map((skill, index) => ({
    name: skill.title,
    color: skillColors[index % skillColors.length],
    value: 1,
    text: skill.description,
  }));
}

const RADIAN = Math.PI / 180;

export const FutureSkills: React.FC = () => {
  const laptop = useMediaQuery("(max-width: 1450px)");
  const matches = useMediaQuery("(max-width: 1100px)");

  const { report, reportId } = useReportStore();

  const content = report?.skills_to_master || [];

  const futureSkills = mapRoadmapSkillsToMaster(content.data);

  const renderLabel: PieLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    text,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // якщо тексту немає — змістити трохи нижче
    const offsetY = !text ? (matches ? 20 : 40) : 0;

    return (
      <foreignObject
        x={x - (matches ? 70 : 140)}
        y={y - (matches ? 40 : 100) + offsetY}
        width={matches ? 140 : 280}
        height={matches ? 80 : 220}
        style={{
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Title
            size={matches ? 14 : 18}
            c="#FFF"
            mb={text ? 8 : 0}
            style={{
              maxWidth: matches ? "100px" : "220px",
              wordBreak: "break-word",
              overflow: "hidden",
              margin: "0 auto",
            }}
          >
            {name}
          </Title>
          {text && !matches && (
            <Text
              c="#FFF"
              size="sm"
              style={{
                maxWidth: "220px",
                wordBreak: "break-word",
                overflow: "hidden",
                margin: "0 auto",
              }}
            >
              {text}
            </Text>
          )}
        </div>
      </foreignObject>
    );
  };

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title
        size={matches ? 24 : 36}
        mb={8}
        style={{ wordBreak: "break-word" }}
      >
        {content?.mark_down ? content?.mark_down?.replace("##", "") : ""}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {content?.explanation ? content?.explanation : ""}
      </Text>
      <ResponsiveContainer
        width="100%"
        height={matches ? 314 : laptop ? 800 : 1056}
      >
        <PieChart>
          <Pie
            data={futureSkills}
            cx="50%"
            cy="50%"
            outerRadius={matches ? 139 : laptop ? 360 : 528}
            innerRadius={matches ? 25 : laptop ? 100 : 200}
            label={renderLabel}
            labelLine={false}
            dataKey="value"
            startAngle={45}
            endAngle={450}
          >
            {futureSkills.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {laptop &&
        futureSkills.map((item, index) => (
          <Flex gap={5} mt={16} key={index}>
            <ColorSwatch color={item.color} size={14} radius={2} mt={5} />
            <Text>
              <b>{item.name}</b>: {item.text}
            </Text>
          </Flex>
        ))}
      <LikeResponse report_id={reportId} section="roadmap_skills_to_master" />
    </Paper>
  );
};
